import { useCallback, useEffect, useState } from "react";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Photo } from "@/components/Photo";
import { toast } from "sonner";

export default function PhotosTab({ reload }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/portal/photo-queue");
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (aid, release) => {
    try {
      await api.post(`/portal/photo-queue/${aid}/decide`, {
        release, note: release ? "Released by a director" : "Kept back by a director" });
      toast.success(release ? "Shared with the family" : "Kept back");
      await load();
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setConsent = async (sid, consent) => {
    try {
      await api.put(`/portal/photo-consent/${sid}`, { consent, note: "Set from portal controls" });
      toast.success(consent ? "Photo sharing allowed" : "Photo sharing not allowed");
      await load();
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-muted-foreground" data-testid="photos-loading">Loading photos…</p>;

  return (
    <div data-testid="photos-tab">
      <h2 className="text-base md:text-lg font-bold">Held photos</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        A photo is held whenever a child in it has no recorded media consent, or consent was refused. Nothing
        is shared by assumption.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.held.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="no-held-photos">
            No photos are being held back.
          </p>
        )}
        {data.held.map((h) => (
          <div key={h.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`held-photo-${h.id}`}>
            <Photo path={h.photo_path} alt={h.detail || "Held photo"}
                   className="rounded-xl w-full h-44 object-cover" testid={`held-img-${h.id}`} />
            <p className="mt-3 text-sm font-bold">{h.student_name}</p>
            <p className="text-xs text-muted-foreground">{h.hold_reason}</p>
            <p className="text-xs text-muted-foreground">{dateTimeOf(h.at)} · {h.staff}</p>
            {h.detail && <p className="mt-2 text-sm">{h.detail}</p>}
            <div className="mt-4 flex gap-2">
              <Button size="sm" onClick={() => decide(h.id, true)}
                      className="flex-1 rounded-full font-bold bg-[#10B981] hover:bg-[#059669]"
                      data-testid={`release-${h.id}`}>
                Share
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide(h.id, false)}
                      className="flex-1 rounded-full font-bold" data-testid={`keep-back-${h.id}`}>
                Keep back
              </Button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-10 text-base md:text-lg font-bold">Media consent per child</h2>
      <div className="mt-5 bg-white rounded-3xl border border-border overflow-hidden">
        {data.consent.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-4 px-5 py-4 border-t border-border first:border-0"
               data-testid={`consent-row-${c.id}`}>
            <div>
              <p className="text-sm font-bold">{c.name}</p>
              <p className="text-xs text-muted-foreground">
                {c.room || "No classroom"} · {c.status} ·{" "}
                {c.media_consent === true ? "photos may be shared"
                  : c.media_consent === false ? "photos not shared" : "no decision recorded"}
              </p>
            </div>
            <Switch checked={c.media_consent === true} onCheckedChange={(v) => setConsent(c.id, v)}
                    data-testid={`admin-consent-${c.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
